/**
 * RTWO Native Currency Engine — Native Currency Validation Service
 */

import type { CurrencyId, FeePolicyId, PolicyId } from '../domain-models';
import type {
  INativeCurrencyValidationService,
  NativeCurrencyValidationResult,
} from '../service-interfaces';
import type {
  LedgerAdapterRegistry,
  ProtocolAdapterRegistry,
} from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

export class NativeCurrencyValidationService
  extends BaseNATVService
  implements INativeCurrencyValidationService
{
  readonly serviceId = 'natv-validation-service-001';
  readonly serviceName = 'Native Currency Validation Service';
  readonly description =
    'Native currency validation service — placeholder, requires real Validation Backend. No fake validation results.';
  readonly integrations = ['Core Engine', 'Blockchain Protocol Engine', 'Ledger Engine'];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = true;

  constructor(
    context: NATVServiceContext,
    private readonly ledgerRegistry: LedgerAdapterRegistry,
    private readonly protocolRegistry: ProtocolAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_genesis_configuration';
  }

  async validateCurrency(currencyId: CurrencyId): Promise<NativeCurrencyValidationResult> {
    if (!this.ledgerRegistry.isConfigured() || !this.protocolRegistry.isConfigured()) {
      return {
        isValid: false,
        errors: ['Currency validation requires real Validation Backend — Not Configured'],
        isPlaceholder: true,
        source: 'Native Currency Engine — Validation Backend Not Configured',
      };
    }

    const ledgerResult = await this.ledgerRegistry.getActiveAdapter().validateCurrency(currencyId);
    const protocolResult = await this.protocolRegistry
      .getActiveAdapter()
      .validateCurrency(currencyId);

    if (ledgerResult || protocolResult) {
      this.context.eventBus.emit(NATVInternalEventTypes.CURRENCY_VALIDATED, { currencyId });
    }

    return {
      isValid: false,
      errors: ['Currency validation requires real Validation Backend — Not Configured'],
      isPlaceholder: true,
      source: 'Native Currency Engine — Validation Backend Not Configured',
    };
  }

  async validateMonetaryPolicy(policyId: PolicyId): Promise<NativeCurrencyValidationResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        isValid: false,
        errors: ['Monetary policy validation requires real Validation Backend — Not Configured'],
        isPlaceholder: true,
        source: 'Native Currency Engine — Validation Backend Not Configured',
      };
    }

    await this.ledgerRegistry.getActiveAdapter().validateMonetaryPolicy(policyId);

    return {
      isValid: false,
      errors: ['Monetary policy validation requires real Validation Backend — Not Configured'],
      isPlaceholder: true,
      source: 'Native Currency Engine — Validation Backend Not Configured',
    };
  }

  async validateFeePolicy(feePolicyId: FeePolicyId): Promise<NativeCurrencyValidationResult> {
    if (!this.protocolRegistry.isConfigured()) {
      return {
        isValid: false,
        errors: ['Fee policy validation requires real Validation Backend — Not Configured'],
        isPlaceholder: true,
        source: 'Native Currency Engine — Validation Backend Not Configured',
      };
    }

    await this.protocolRegistry.getActiveAdapter().validateFeePolicy(feePolicyId);

    return {
      isValid: false,
      errors: ['Fee policy validation requires real Validation Backend — Not Configured'],
      isPlaceholder: true,
      source: 'Native Currency Engine — Validation Backend Not Configured',
    };
  }
}
